import React, { useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';

import { Badge } from '@prisjakt/ui/badge';
import { FontEmphasis } from '@prisjakt/ui/text';
import { CaptionText, TitleSmallText } from '@prisjakt/ui/text/variants';

import { MediumUp, MobileOnly } from 'src/ui/block';
import { usePriceRowContext } from '../../../price-row-context';

import {
  ShippingType,
  isStockStatusSupported,
  getShippingDays,
  getStandardizedDeliveryText,
  getStockStatusBadgeColor,
  getStockStatusIcon,
  StockStatusEnum,
} from './utils';

import { ShippingCollection, StockStatus } from 'src/types';
import { StockStatusTranslationKey } from 'src/components/stock-status/types';

import { StyledCaptionText, StyledDeliveryTime, Wrapper } from './styles';

interface IDeliveryTimeProps {
  shipping?: ShippingCollection | null;
  stockStatus?: StockStatus | null;
}

export const DeliveryTime: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IDeliveryTimeProps>
>> = props => {
  const { shipping, stockStatus } = props;

  const { t } = useContext(AppContext);

  const { forceMobileView, newOfferItemExperiment } = usePriceRowContext();

  const label =
    stockStatus === StockStatusEnum.IN_STOCK ||
    stockStatus === StockStatusEnum.INCOMING
      ? `${t('product', 'delivery-time.label')}: `
      : '';
  const shippingDays = getShippingDays(t, ShippingType.CHEAPEST, shipping);
  const standardizedDeliveryText = getStandardizedDeliveryText(t, stockStatus);
  const deliveryTimeShort = isStockStatusSupported(stockStatus) && shippingDays;
  const deliveryTimeLong =
    label + (deliveryTimeShort ? shippingDays : standardizedDeliveryText);

  const getStockStatusBadgeText = () => {
    if (shippingDays) return null;
    // For experiment, in_stock should never have badge text. StandardizedDeliveryText
    // returns baseline value for in_stock, so that's covered here.
    if (stockStatus === StockStatusEnum.IN_STOCK) return null;
    return t(
      'product',
      `stock-status.${stockStatus || 'unknown'}${newOfferItemExperiment &&
        '-experiment'}` as StockStatusTranslationKey
    );
  };

  return (
    <StyledDeliveryTime>
      {deliveryTimeShort && !newOfferItemExperiment && (
        <MobileOnly>
          <CaptionText emphasis={FontEmphasis.MEDIUM}>
            <span>{deliveryTimeShort}</span>
          </CaptionText>
        </MobileOnly>
      )}
      {newOfferItemExperiment && (
        <MobileOnly>
          <Wrapper>
            <Badge
              color={getStockStatusBadgeColor(stockStatus)}
              icon={getStockStatusIcon(stockStatus)}
              text={getStockStatusBadgeText()}
            />
            {shippingDays && (
              <div>
                <TitleSmallText emphasis={FontEmphasis.MEDIUM}>
                  {label}
                </TitleSmallText>
                <TitleSmallText emphasis={FontEmphasis.HIGH}>
                  {shippingDays}
                </TitleSmallText>
              </div>
            )}
          </Wrapper>
        </MobileOnly>
      )}
      {!forceMobileView && deliveryTimeLong && (
        <MediumUp>
          <StyledCaptionText emphasis={FontEmphasis.MEDIUM}>
            <span>{deliveryTimeLong}</span>
          </StyledCaptionText>
        </MediumUp>
      )}
    </StyledDeliveryTime>
  );
};
